import { useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useCurrentPageContext } from '../../lib/page-context';
import { getPageHelp, type PageHelp } from '../../lib/page-help';
import { Icon } from '../ui/Icon';
import { ChatView } from './ChatView';
import styles from './FloatingComposePanel.module.css';

export function FloatingComposePanel() {
  const context = useCurrentPageContext();
  const href = useRouterState({ select: (state) => state.location.href });
  const details = context.pageContext.pageSpecificContext;
  const help = getPageHelp(context.pageContext.path, details);
  // Help belongs to this visit, including in-page setup/editor and direction
  // changes. Remounting also isolates late callbacks from a previous chat;
  // its server-owned conversation remains available in Recent chats.
  const scope = help
    ? help.scope
      ? JSON.stringify([href, help.scope])
      : JSON.stringify([href, details?.direction ?? 'outgoing', details?.view ?? '', details?.starter ?? ''])
    : context.pageContext.path;
  return <PageComposePanel key={scope} context={context} help={help} />;
}

function PageComposePanel({ context, help }: {
  context: ReturnType<typeof useCurrentPageContext>;
  help: PageHelp | undefined;
}) {
  const { pageContext, pageType, scopeLabel } = context;
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const panel = useRef<HTMLElement>(null);
  const composeButton = useRef<HTMLButtonElement>(null);

  const isAvailable = Boolean(help) || (pageType !== 'other' && pageType !== 'daily-brief');

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.repeat) return;
      event.preventDefault();
      setIsOpen(false);
      composeButton.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (panel.current) panel.current.inert = !isOpen;
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      panel.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  if (!isAvailable) return null;

  return (
    <>
      <button
        ref={composeButton}
        type="button"
        className={`${styles.composeButton} ${help ? styles.helpButton : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label={help ? `Help with ${help.title}` : 'Compose with Resonance'}
        aria-expanded={isOpen}
        aria-controls="resonance-compose-panel"
        title={help ? `Help with ${help.title}` : 'Compose with Resonance'}
      >
        <Icon name={help ? 'sparkles' : 'edit'} size={23} />
        {help && <span>Help</span>}
      </button>

      <aside
        ref={panel}
        id="resonance-compose-panel"
        className={styles.panel}
        data-open={isOpen}
        aria-hidden={!isOpen}
        aria-label="Resonance chat"
      >
        <header className={styles.header}>
          <div className={styles.heading}>
            <span className={styles.title}>
              <Icon name="sparkles" size={17} />
              {help ? 'Ask Echo' : 'Ask Resonance'}
            </span>
            <span className={styles.scope}>{help ? help.title : `Scoped to ${scopeLabel}`}</span>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              setIsOpen(false);
              composeButton.current?.focus();
            }}
            aria-label="Close chat"
            title="Close"
          >
            <Icon name="x" size={19} />
          </button>
        </header>
        <div className={styles.chat}>
          <ChatView
            key={pageContext.path}
            conversationId={conversationId}
            pageContext={pageContext}
            onConversationSettled={setConversationId}
            {...(help ? {
              placeholder: help.placeholder ?? 'Ask anything…',
              intro: ({ sendMessage, running, isEmpty }: { sendMessage: (text: string) => void; running: boolean; isEmpty: boolean }) => isEmpty ? (
                <HelpIntroduction help={help} active={isOpen} onAsk={sendMessage} disabled={running} />
              ) : null,
            } : {})}
          />
        </div>
      </aside>
    </>
  );
}

function HelpIntroduction({ help, active, onAsk, disabled }: {
  help: PageHelp;
  active: boolean;
  onAsk: (text: string) => void;
  disabled: boolean;
}) {
  const [visibleWords, setVisibleWords] = useState(0);
  const words = help.introduction.split(' ');
  useEffect(() => {
    if (!active) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisibleWords(words.length);
      return;
    }
    setVisibleWords(0);
    const timer = window.setInterval(() => setVisibleWords((count) => {
      if (count + 3 >= words.length) window.clearInterval(timer);
      return Math.min(count + 3, words.length);
    }), 40);
    return () => window.clearInterval(timer);
  }, [active, help.introduction, words.length]);

  return (
    <section className={styles.helpIntro} aria-label={`${help.title} guide`}>
      <h2>{help.heading ?? 'Let’s make this useful for you.'}</h2>
      <p>
        <span className={styles.screenReaderOnly}>{help.introduction}</span>
        <span aria-hidden="true">{words.slice(0, visibleWords).join(' ') || '\u00a0'}</span>
      </p>
      <div className={styles.examples}>
        {help.examples.map((example) => (
          <button key={example} type="button" disabled={disabled} onClick={() => onAsk(example)}>
            {example}<Icon name="arrow-up-right" size={16} />
          </button>
        ))}
      </div>
    </section>
  );
}
